import { Copy, ExternalLink, Shield, Search, FileText, Globe, AlertTriangle, Database, Folder, Cloud, Cpu, BookmarkPlus } from "lucide-react";
import { GeneratedDork } from "@/types/dork";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

interface DorkCardProps {
  dork: GeneratedDork;
  index: number;
  onSave?: (dork: GeneratedDork) => void;
}

const categoryConfig: Record<string, { icon: any; color: string; label: string }> = {
  osint: { icon: Search, color: "text-neon-cyan", label: "OSINT" },
  security: { icon: Shield, color: "text-neon-green", label: "Security" },
  academic: { icon: FileText, color: "text-purple-400", label: "Academic" },
  "file-discovery": { icon: FileText, color: "text-orange-400", label: "File Discovery" },
  research: { icon: Globe, color: "text-blue-400", label: "Research" },
  vulnerabilities: { icon: AlertTriangle, color: "text-red-400", label: "Vulnerabilities" },
  databases: { icon: Database, color: "text-yellow-400", label: "Databases" },
  "sensitive-directories": { icon: Folder, color: "text-amber-400", label: "Sensitive Dirs" },
  cloud: { icon: Cloud, color: "text-sky-400", label: "Cloud" },
  iot: { icon: Cpu, color: "text-teal-400", label: "IoT" },
  custom: { icon: Globe, color: "text-muted-foreground", label: "Custom" },
};

const riskConfig: Record<string, { color: string; bg: string }> = {
  low: { color: "text-green-400", bg: "bg-green-400/10 border-green-400/20" },
  medium: { color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/20" },
  high: { color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20" },
  critical: { color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
};

export function DorkCard({ dork, index, onSave }: DorkCardProps) {
  const { t } = useLocale();
  const category = categoryConfig[dork.category] || categoryConfig.custom;
  const risk = riskConfig[dork.risk] || riskConfig.low;
  const CategoryIcon = category.icon;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(dork.query);
    toast({ title: t("dork_card.copied_title"), description: t("dork_card.copied_desc") });
  };

  const handleOpenSearch = () => {
    const q = dork.query.trim();
    if (!q) return;
    const url = `https://www.google.com/search?q=${encodeURIComponent(q)}`;
    if (typeof window !== "undefined" && (window as any).Telegram?.WebApp?.openLink) {
      (window as any).Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="dork-card group animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <CategoryIcon className={`w-4 h-4 ${category.color}`} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${category.color}`}>
            {category.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${risk.bg} ${risk.color}`}>
            {dork.risk}
          </span>
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < dork.effectiveness ? "bg-neon-green" : "bg-muted"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Query */}
      <div className="bg-cyber-darker rounded-lg p-3 mb-3 border border-border/30">
        <code className="text-sm font-mono text-foreground break-all leading-relaxed">{dork.query}</code>
      </div>

      {/* Purpose */}
      <p className="text-sm text-muted-foreground mb-2 leading-relaxed">{dork.purpose}</p>

      {/* Description */}
      {dork.description && (
        <p className="text-xs text-muted-foreground/70 mb-3 leading-relaxed">{dork.description}</p>
      )}

      {/* Examples */}
      {dork.examples && dork.examples.length > 0 && (
        <div className="mb-3">
          <span className="text-xs font-medium text-muted-foreground">{t("dork_card.examples")}</span>
          <ul className="mt-1 space-y-1">
            {dork.examples.map((ex, i) => (
              <li key={i} className="text-xs text-muted-foreground/60 pl-3 border-l border-border/50">{ex}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Targeted Services */}
      {dork.targetedServices && dork.targetedServices.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {dork.targetedServices.map((service, idx) => (
            <span key={idx} className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full border border-primary/20">
              {service}
            </span>
          ))}
        </div>
      )}

      {/* Operators */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {dork.operators.map((op, idx) => (
          <span key={idx} className="px-2 py-0.5 text-xs font-mono bg-muted/50 text-foreground/80 rounded border border-border/30">
            {op || '""'}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={handleCopy} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-muted/50 hover:bg-muted text-foreground text-sm font-medium transition-all border border-border/30 hover:border-primary/30">
          <Copy className="w-4 h-4" /> {t("dork_card.copy")}
        </button>
        <button
          onClick={handleOpenSearch}
          disabled={!dork.query.trim()}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 hover:from-primary/30 hover:to-secondary/30 text-foreground text-sm font-medium transition-all border border-primary/30 hover:border-primary/50 hover:shadow-neon disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Open Google search for this dork"
        >
          <ExternalLink className="w-4 h-4" /> {t("dork_card.search")}
        </button>
        {onSave && (
          <button onClick={() => onSave(dork)} className="flex items-center justify-center px-3 py-2.5 rounded-lg bg-secondary/10 hover:bg-secondary/20 text-secondary transition-all border border-secondary/20 hover:border-secondary/40">
            <BookmarkPlus className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
